'use client';

import * as React from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'tw-bg-background group/calendar p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('tw-w-fit', defaultClassNames.root),
        months: cn(
          'tw-flex tw-gap-4 tw-flex-col tw-md:tw-flex-row tw-relative',
          defaultClassNames.months,
        ),
        month: cn('tw-flex tw-flex-col tw-w-full tw-gap-4', defaultClassNames.month),
        nav: cn(
          'tw-flex tw-items-center tw-w-full tw-absolute tw-top-[1px] tw-inset-x-0 tw-justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          ' tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0 tw-border tw-border-input tw-shadow-sm hover:tw-bg-accent hover:tw-text-accent-foreground  tw-bg-transparent tw-p-0 tw-opacity-50 hover:tw-opacity-100 ',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          ' tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0 tw-border tw-border-input tw-shadow-sm hover:tw-bg-accent hover:tw-text-accent-foreground  tw-bg-transparent tw-p-0 tw-opacity-50 hover:tw-opacity-100 ',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'tw-flex tw-items-center tw-justify-center tw-h-(--cell-size) tw-w-full tw-px-(--cell-size) tw-mt-2',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'tw-w-full tw-flex tw-items-center tw-text-sm tw-font-medium tw-justify-center tw-h-(--cell-size) tw-mt-[-8px] tw-gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'tw-relative tw-has-focus:tw-border-ring tw-border tw-border-input tw-shadow-xs tw-has-focus:tw-ring-ring/50 tw-has-focus:tw-ring-[3px] tw-rounded-md',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('tw-absolute tw-inset-0 tw-opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'tw-select-none tw-font-medium',
          captionLayout === 'label'
            ? 'tw-text-sm'
            : 'tw-rounded-md tw-pl-2 tw-pr-1 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-h-8 [&>svg]:tw-text-muted-foreground [&>svg]:tw-size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'tw-w-full tw-border-collapse',
        weekdays: cn('tw-flex', defaultClassNames.weekdays),
        weekday: cn(
          'tw-text-muted-foreground tw-rounded-md tw-flex-1 tw-font-normal tw-text-[0.8rem] tw-select-none',
          defaultClassNames.weekday,
        ),
        week: cn('tw-flex tw-w-full tw-mt-2', defaultClassNames.week),
        week_number_header: cn(
          'tw-select-none tw-w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'tw-text-[0.8rem] tw-select-none tw-text-muted-foreground',
          defaultClassNames.week_number,
        ),
        range_start: cn('tw-rounded-l-md tw-bg-accent', defaultClassNames.range_start),
        day: 'rdp-button_reset rdp-button  tw-bg-transparent  tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0 hover:tw-bg-accent hover:tw-text-accent-foreground tw-h-8 tw-w-8 tw-p-0 tw-font-normal aria-selected:tw-opacity-100',
        range_end: 'tw-day-range-end',
        selected:
          'tw-bg-zinc-900 tw-text-zinc-50 hover:tw-bg-zinc-900 hover:tw-text-zinc-50 focus:tw-bg-zinc-900 focus:tw-text-zinc-50 dark:tw-bg-zinc-50 dark:tw-text-zinc-900 dark:hover:tw-bg-zinc-50 dark:hover:tw-text-zinc-900 dark:focus:tw-bg-zinc-50 dark:focus:tw-text-zinc-900 !tw-text-white',
        today: 'tw-bg-zinc-100 tw-text-zinc-900 dark:tw-bg-zinc-800 dark:tw-text-zinc-50',
        outside:
          'rdp-button_reset rdp-button tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0 hover:tw-bg-accent hover:tw-text-accent-foreground tw-h-8 tw-w-8 tw-p-0 tw-font-normal aria-selected:tw-opacity-100 tw-day-outside tw-text-muted-foreground aria-selected:tw-bg-accent/50 aria-selected:tw-text-color-white',
        disabled: 'tw-text-zinc-500 tw-opacity-50 dark:tw-text-zinc-400',
        range_middle:
          'aria-selected:tw-bg-zinc-100 aria-selected:tw-text-zinc-900 dark:aria-selected:tw-bg-zinc-800 dark:aria-selected:tw-text-zinc-50',
        hidden: 'tw-invisible',
        day_button: 'tw-bg-transparent',
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot='calendar' ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-4', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-4', className)} {...props} />;
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
        },
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className='flex size-(--cell-size) items-center justify-center text-center'>
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
